/**
 * Образец цвета.
 *
 * Четыре вида миниатюры, потому что врач выбирает цвет по картинке:
 *
 *   SOLID   — сплошная заливка;
 *   GLITTER — основа с крапинами одного цвета. Сплошной заливкой «Серебро
 *             с блёстками» и обычный серый выглядели бы одинаково;
 *   IRIS    — разноцветные блёстки. У «Ириса» крапины разных оттенков,
 *             и от обычных блёсток он отличается именно этим;
 *   RAINBOW — радуга косыми полосами: «Особые цвета».
 *
 * Служебный «Цвет не указан» рисуется штриховкой: сплошной квадрат любого
 * оттенка читался бы как выбранный цвет.
 *
 * Крапины расставляются ДЕТЕРМИНИРОВАННО — по идентификатору цвета. Иначе
 * при каждой перерисовке они прыгали бы, и список мерцал бы при прокрутке.
 */

export type ColorFill = 'SOLID' | 'GLITTER' | 'IRIS' | 'RAINBOW';

export interface SwatchColor {
  id?: string;
  hex: string;
  image?: string | null;
  unspecified?: boolean;
  fill?: ColorFill;
  glitterHex?: string;
}

/** Палитра крапин «Ириса» — разноцветный перламутр. */
const IRIS_COLORS = ['#F4B8D0', '#B8D8F4', '#C9F4B8', '#F4EAB8', '#D8B8F4', '#B8F4EC'];

interface Speck {
  cx: number;
  cy: number;
  color: string;
}

/**
 * Раскладка крапин: РЕГУЛЯРНАЯ сетка со смещением через ряд.
 *
 * Случайные позиции и разные размеры давали комкование — где-то пусто, где-то
 * пятно, и на маленькой миниатюре это читалось как грязь, а не как блёстки.
 * Ровная сетка покрывает площадь равномерно, а смещение нечётных рядов не даёт
 * ей выглядеть таблицей.
 *
 * Цвета «Ириса» перебираются по кругу — так все оттенки распределены поровну,
 * а не собираются случайными кучками.
 */
function buildSpecks(fill: ColorFill, glitterHex: string, cols: number): Speck[] {
  const specks: Speck[] = [];
  const step = 100 / cols;

  for (let row = 0; row < cols; row += 1) {
    // Нечётные ряды сдвинуты на полшага — шахматный порядок вместо решётки.
    const offset = row % 2 === 0 ? step / 2 : step;
    for (let col = 0; col < cols; col += 1) {
      const cx = col * step + offset;
      if (cx > 100) continue;
      specks.push({
        cx,
        cy: row * step + step / 2,
        color: fill === 'IRIS' ? IRIS_COLORS[specks.length % IRIS_COLORS.length]! : glitterHex,
      });
    }
  }

  return specks;
}

export function Swatch({ color, size = 14 }: { color: SwatchColor | undefined; size?: number }) {
  if (!color) return null;

  const radius = Math.max(3, Math.round(size * 0.28));

  if (color.unspecified) {
    return (
      <span
        // Подпись при наведении: в таблице заказов слова «Цвет не указан»
        // не печатаются — они не влезали в колонку, — и штриховка остаётся
        // единственным знаком. Пусть она умеет себя назвать.
        title="Цвет не указан"
        style={{
          display: 'inline-block',
          width: size,
          height: size,
          borderRadius: radius,
          flexShrink: 0,
          border: '1px solid rgba(201,133,44,0.53)',
          background:
            'repeating-linear-gradient(45deg, #FDEBD3, #FDEBD3 2px, #F0B25E 2px, #F0B25E 4px)',
        }}
      />
    );
  }

  if (color.image) {
    return (
      <span
        style={{
          display: 'inline-block',
          width: size,
          height: size,
          borderRadius: radius,
          flexShrink: 0,
          border: '1px solid rgba(0,0,0,0.15)',
          backgroundImage: `url(${color.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
    );
  }

  const fill = color.fill ?? 'SOLID';

  /**
   * Радуга — «Особые цвета», позиция Rainbow.
   *
   * Полосы идут ПО ДИАГОНАЛИ: на квадратике в одиннадцать пикселей
   * горизонтальные полосы сливаются в кашу, а косые читаются как радуга
   * даже в строке таблицы заказов. До этого позиция была обычным серым
   * квадратом и от «Цвет не указан» отличалась только подписью.
   *
   * Переходы РЕЗКИЕ, а не плавные: плавные на маленьком образце дают
   * грязноватую смесь, а радуга узнаётся именно чередованием полос.
   */
  if (fill === 'RAINBOW') {
    const bands = ['#E53935', '#FB8C00', '#FDD835', '#43A047', '#1E88E5', '#8E24AA'];
    const step = 100 / bands.length;
    const stops = bands
      .map((hex, index) => `${hex} ${index * step}%, ${hex} ${(index + 1) * step}%`)
      .join(', ');

    return (
      <span
        style={{
          display: 'inline-block',
          width: size,
          height: size,
          borderRadius: radius,
          flexShrink: 0,
          border: '1px solid rgba(0,0,0,0.15)',
          background: `linear-gradient(135deg, ${stops})`,
        }}
      />
    );
  }

  if (fill === 'SOLID') {
    return (
      <span
        style={{
          display: 'inline-block',
          width: size,
          height: size,
          borderRadius: radius,
          flexShrink: 0,
          border: '1px solid rgba(0,0,0,0.15)',
          background: color.hex,
        }}
      />
    );
  }

  /**
   * Плотность сетки растёт с размером миниатюры.
   *
   * На 11-пиксельном образце в таблице заказов крапины из плотной сетки
   * сливаются в сплошной тон; на крупном, наоборот, редкая сетка выглядит
   * дырявой. Радиус привязан к шагу, поэтому крапины всегда одного размера
   * относительно друг друга и не наезжают.
   */
  const cols = size < 16 ? 5 : size < 30 ? 7 : 9;
  const speckRadius = (100 / cols) * 0.3;
  const specks = buildSpecks(fill, color.glitterHex ?? '#FFFFFF', cols);

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      style={{ flexShrink: 0, borderRadius: radius, border: '1px solid rgba(0,0,0,0.15)' }}
      aria-hidden
    >
      <rect width="100" height="100" fill={color.hex} />
      {specks.map((speck, index) => (
        <circle
          key={index}
          cx={speck.cx}
          cy={speck.cy}
          r={speckRadius}
          fill={speck.color}
          opacity={0.92}
        />
      ))}
    </svg>
  );
}

/** Подпись вида миниатюры — для списков и подсказок. */
export const FILL_LABELS: Record<ColorFill, string> = {
  SOLID: 'Сплошная заливка',
  GLITTER: 'Блёстки',
  IRIS: 'Ирис — разноцветные блёстки',
  RAINBOW: 'Радуга',
};
